import {Tooltip} from '@mantine/core';
import React from 'react';

import EntryActions from 'actions/EntryActions';
import BenchlingConfig from 'BenchlingConfig';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible, accessibleObjs} from 'graphql-client/InaccessibleInfo';
import CreateEntryFromTemplateModal from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import LegacyCreateEntryFromTemplateModalClass from 'notebook/entry-templates/LegacyCreateEntryFromTemplateModal';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {
  TemplatePublishingRecordsQuery,
  TemplatePublishingRecordsQueryVariables,
} from 'template-collectibles/publishing-ui/graphql/__generated/TemplatePublishingRecordsQuery';
import {TEMPLATE_PUBLISHING_RECORDS_QUERY} from 'template-collectibles/publishing-ui/graphql/TemplatePublishingRecordsQuery';
import {getEffectivePublishingRecord} from 'template-collectibles/publishing-ui/TemplatePublishingUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';

export const CREATE_ENTRY_BUTTON_TEXT = 'Create entry';

export const ARCHIVED_TEMPLATE_MESSAGE = 'To create an entry, please unarchive this template';

export const LATEST_VERSION_REQUIRED_MESSAGE =
  'To create an entry, please select the latest version of this template';

export const EFFECTIVE_VERSION_REQUIRED_MESSAGE =
  'To create an entry, please select an effective version of this template';

export const NO_EFFECTIVE_VERSION_MESSAGE = 'To create an entry, please publish a version of this template';

/**
 * Returns the reason the effective version requirement blocks entry creation, or `null` if it doesn't. Only
 * meaningful when template publishing is enabled: entries are then created from the effective version, which
 * may sit underneath newer drafts or may not exist at all.
 */
function getEffectiveVersionMessage(
  result: SkippableBenchlingQueryResult<
    TemplatePublishingRecordsQuery,
    TemplatePublishingRecordsQueryVariables
  >,
  openVersionApiId: string | undefined
): string | null {
  // Until the publishing records are known, leave the button alone rather than guessing at a reason.
  if (result.type !== QueryResultType.SUCCESS || isInaccessible(result.data.template)) {
    return null;
  }
  const effectiveRecord = getEffectivePublishingRecord(
    accessibleObjs(result.data.template.publishingRecords)
  );
  if (effectiveRecord == null) {
    return NO_EFFECTIVE_VERSION_MESSAGE;
  }
  return effectiveRecord.publishableVersionId === openVersionApiId
    ? null
    : EFFECTIVE_VERSION_REQUIRED_MESSAGE;
}

function handleCreateEntryFromTemplate({
  entryName,
  folderId,
  templateId,
  tagSchemaId,
  schemaTags,
  initialDay,
}: {
  entryName: string;
  folderId: string;
  templateId: string;
  tagSchemaId?: string;
  schemaTags?: TagUpdateObject;
  initialDay: string;
}): void {
  EntryActions.createFromTemplate({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
    /* eslint-disable-next-line @typescript-eslint/unbound-method */
    onSuccess: LegacyCreateEntryFromTemplateModalClass.close,
  });
}

interface Props {
  templateId: string;
  isTemplateArchived: boolean;
  /**
   * API identifier of the template version currently open in the editor, or `undefined` before the version
   * list has loaded.
   */
  openVersionApiId: string | undefined;
  isViewingLatestVersion: boolean;
}

/**
 * Opens the create-entry-from-template modal for a template.
 *
 * Entries are only ever created from one specific version of the template — the latest version, or the
 * effective version when publishing is enabled. The button is disabled while the user views any other
 * version so that the entry they get always matches the template content in front of them, and while the
 * template is archived. Whichever reason applies is explained in a tooltip.
 */
export default function CreateEntryFromTemplateButton({
  templateId,
  isTemplateArchived,
  openVersionApiId,
  isViewingLatestVersion,
}: Props): JSX.Element {
  const isPublishingEnabled = BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING;
  const publishingRecordsResult = useSkippableBenchlingQuery<
    TemplatePublishingRecordsQuery,
    TemplatePublishingRecordsQueryVariables
  >(TEMPLATE_PUBLISHING_RECORDS_QUERY, {variables: {templateId}, skip: !isPublishingEnabled});

  // Being archived blocks entry creation outright, so it takes precedence over the version reasons:
  // pointing an archived template's reader at a different version would not get them any closer.
  let disabledMessage: string | null;
  if (isTemplateArchived) {
    disabledMessage = ARCHIVED_TEMPLATE_MESSAGE;
  } else if (isPublishingEnabled) {
    disabledMessage = getEffectiveVersionMessage(publishingRecordsResult, openVersionApiId);
  } else {
    disabledMessage = isViewingLatestVersion ? null : LATEST_VERSION_REQUIRED_MESSAGE;
  }

  const showModal = (): void => {
    // TODO(BNCH-38468): Remove feature flag after rollout and discontinue lagacy modal
    if (!BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL) {
      LegacyCreateEntryFromTemplateModalClass.open({
        initialTemplateId: templateId,
        initialFolderId: FolderDefaults.get(FolderDefaultsKey.NOTEBOOK),
        onRequestSubmit: handleCreateEntryFromTemplate,
      });
    } else {
      legacyRunModal(CreateEntryFromTemplateModal, {
        initialTemplateId: templateId,
        initialFolderId: FolderDefaults.get(FolderDefaultsKey.NOTEBOOK),
        onRequestClose: () => {},
        onRequestSubmit: handleCreateEntryFromTemplate,
        isTemplateSelectorDisabled: false,
      });
    }
  };

  return (
    <Tooltip
      label={disabledMessage ?? ''}
      disabled={disabledMessage == null}
      multiline={true}
      width={240}
      // The toolbar panel clips its overflow, which would cut the label off at the panel edge.
      withinPortal={true}
    >
      {/* Tooltip attaches its reference ref to its child, and ui-library's Button is not a forwardRef
          component, so it needs a wrapper to hang that ref on. Keep the wrapper a block element so the
          button still sizes itself to the full panel width. */}
      <div>
        <Button
          id="create-entry-button"
          buttonStyle={ButtonStyle.SECONDARY}
          fullWidth={true}
          size={ButtonSize.SMALL}
          disabled={disabledMessage != null}
          onClick={showModal}
          text={CREATE_ENTRY_BUTTON_TEXT}
        />
      </div>
    </Tooltip>
  );
}
